/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { isUserLoggedIn } from '../../utils/utils';
import { retrieve } from '../../utils/storage_helper';
import Button from '../Button';
import envConfig from '../../config/envConfig';

function HeroSection() {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const [isLoggedIn, setIsLoggedIn] = React.useState(false);

    React.useEffect(() => {
        let mounted = true;
        (async () => {
            const result = await isUserLoggedIn();
            if (!mounted) return;
            setIsLoggedIn(!!result);
        })();
        return () => { mounted = false; };
    }, []);

    const userData = isLoggedIn ? retrieve('userData') : {};
    const userName = userData?.user_name || 'User';

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // Heart SVG path for mask
    const heartPath = "M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z";

    return (
        <section
            className="hero-section"
            style={{
                background: '#ffffff',
                padding: '100px 0 80px',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Heart-shaped grid background */}
            <div
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '600px',
                    height: '600px',
                    backgroundImage: `
            linear-gradient(to right, ${theme.primary}40 1px, transparent 1px),
            linear-gradient(to bottom, ${theme.primary}40 1px, transparent 1px)
          `,
                    backgroundSize: '50px 50px',
                    WebkitMaskImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='${heartPath}'/%3E%3C/svg%3E")`,
                    maskImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='${heartPath}'/%3E%3C/svg%3E")`,
                    WebkitMaskSize: 'contain',
                    maskSize: 'contain',
                    WebkitMaskRepeat: 'no-repeat',
                    maskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                    maskPosition: 'center',
                    opacity: 0.25,
                    zIndex: 0
                }}
            />

            <div className="container" style={{ position: 'relative', zIndex: 1 }}>
                {/* Enterprise Ready Badge */}
                <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 16px',
                    background: '#ffffff',
                    border: `2px solid ${theme.primary}`,
                    borderRadius: '50px',
                    marginBottom: '24px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '1.5px',
                    color: theme.primary,
                    boxShadow: `0 2px 8px ${theme.primary}20`
                }}>
                    <div style={{
                        width: '8px',
                        height: '8px',
                        background: theme.primary,
                        borderRadius: '2px'
                    }} />
                    ENTERPRISE READY
                </div>

                <h1 style={{
                    fontSize: '3.5rem',
                    fontWeight: 800,
                    marginBottom: '24px',
                    lineHeight: 1.2,
                    background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                }}>
                    Build Bigger. Ship Faster.
                </h1>

                <p style={{
                    fontSize: '1.2rem',
                    marginBottom: '16px',
                    maxWidth: '800px',
                    margin: '0 auto 16px',
                    lineHeight: 1.7,
                    color: '#333',
                    fontWeight: 500
                }}>
                    <strong>arcee</strong> empowers enterprises to launch complex, scalable applications in days. Get the agility of No-Code with the raw power of a <strong>Headless Django Backend</strong>.
                </p>
                <hr style={{
                    width: '100%',
                    maxWidth: '800px',
                    height: '1px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    background: `linear-gradient(90deg, transparent, ${theme.secondary}, transparent)`,
                    margin: '32px auto',
                    opacity: 0.5
                }} />

                <p style={{
                    fontSize: '1.05rem',
                    marginBottom: '40px',
                    maxWidth: '800px',
                    margin: '0 auto 40px',
                    lineHeight: 1.7,
                    color: '#666'
                }}>
                    <strong>{envConfig.description}</strong>
                </p>

                {isLoggedIn ? (
                    <div>
                        <p style={{ fontSize: '1.2rem', marginBottom: '24px', color: '#666' }}>
                            Logged in as <strong style={{ color: theme.primary }}>{userName}</strong>
                        </p>
                        <Button
                            mode="primary-bold"
                            onClick={() => navigate('/dashboard')}
                        >
                            Go to Dashboard
                        </Button>
                    </div>
                ) : (
                    <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Button
                            mode="primary-bold"
                            onClick={() => scrollToSection('contact')}
                        >
                            Contact Us
                        </Button>
                        <Button
                            mode="outlined"
                            onClick={() => scrollToSection('about')}
                        >
                            See Details
                        </Button>
                    </div>
                )}
            </div>
        </section>
    );
}

export default HeroSection;
